import {
  CoinVertical,
  Fire,
  Gear,
  SpeakerHigh,
  SpeakerX,
  SquareSplitHorizontal,
  SquareSplitVertical,
} from "@phosphor-icons/react";
import confetti, {
  type CreateTypes,
  type Options,
  type Shape,
} from "canvas-confetti";
import clsx from "clsx";
import { AnimatePresence, useReducedMotion, type Variants } from "motion/react";
import { motion } from "motion/react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { match } from "ts-pattern";

import { useLocale } from "~/hooks/locale";
import { useLocalStorage } from "~/hooks/storage";
import { useTheme } from "~/hooks/theme";
import { colors } from "~/theme";
import type { FragmentType } from "~/utils/fragment";

import { useGame } from "../GameContext";
import { Toaster } from "../Toaster";
import type { Game_WordFragment } from "./Game_WordFragment";
import { GameMenu } from "./GameMenu";
import { GameMenuItem } from "./GameMenuItem";
import { GameOption } from "./GameOption";
import { GamePlayContext } from "./GamePlayContext";
import { GameStat } from "./GameStat";
import { Pairs } from "./Pairs";
import { QuestionAnswer } from "./QuestionAnswer";
import { Timer } from "./Timer";
import { type GameAnswer, getPoints, getStreak } from "./utils/answer";

const variants: Variants = {
  active: {
    rotate: 90,
    opacity: 0,
  },
  inactive: {
    rotate: 0,
    opacity: 1,
  },
};

const CONFETTI_BLASTS: Options[] = [
  // blast confetti toward the center from all four corners
  {
    angle: -45,
    origin: {
      x: 0,
      y: 0,
    },
  },
  {
    angle: -135,
    origin: {
      x: 1,
      y: 0,
    },
  },
  {
    angle: 45,
    origin: {
      x: 0,
      y: 1,
    },
  },
  {
    angle: 135,
    origin: {
      x: 1,
      y: 1,
    },
  },
];

export type GameWord = FragmentType<
  "Word",
  { select: typeof Game_WordFragment }
>;

export const Game: React.FC = () => {
  const { t } = useLocale();
  const { game, setGame, resetGame, toggleSound, isSoundEnabled } = useGame();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRow, setIsRow] = useLocalStorage("row", true);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const [answers, setAnswers] = useState<GameAnswer[]>([]);
  const addAnswer = useCallback((answer: GameAnswer) => {
    setAnswers((prev) => [...prev, answer]);
  }, []);

  const streak = useMemo(() => getStreak(answers), [answers]);

  const percentCompleted = useMemo(() => {
    return (
      (game.mode === "pairs"
        ? answers.filter((answer) => answer.correct)
        : answers
      ).length / game.words.length
    );
  }, [game.words, game.mode, answers]);

  const [startedAt, setStartedAt] = useState(Date.now());
  const [rewardTime, setRewardTime] = useState(0);

  const addRewardTime = useCallback(
    (seconds: number) => {
      if (game.settings.isTimeRewardEnabled) {
        setRewardTime((prev) => prev + seconds * 1000);
      }
    },
    [game.settings.isTimeRewardEnabled],
  );

  const endsAt = useMemo(
    () => startedAt + rewardTime + game.timeLimit * 1000,
    [startedAt, game.timeLimit, rewardTime],
  );

  const restartGame = useCallback(() => {
    setStartedAt(Date.now());
    setRewardTime(0);
    setAnswers([]);
    setIsMenuOpen(false);
    toast.dismiss();
  }, []);

  const endGame = useCallback(
    (answer?: GameAnswer) => {
      setGame((prev) =>
        // if time is already set, the game is over
        // setting it a second time would cause the post game screen to rerender
        prev.time > 0
          ? prev
          : {
              ...prev,
              time: Date.now() - startedAt,
              answers: answer ? [...answers, answer] : answers,
            },
      );
    },
    [setGame, startedAt, answers],
  );

  const word = useMemo(
    () => game.words[answers.length],
    [game.words, answers.length],
  );

  const [theme] = useTheme();
  const confettiRef = useRef<CreateTypes>(null);
  const prefersReducedMotion = useReducedMotion();

  const blastConfetti = useCallback(() => {
    if (!confettiRef.current || prefersReducedMotion) {
      return;
    }

    confettiRef.current.reset();

    const color = match(theme)
      .with("hearts", () => colors["can-can"])
      .otherwise(() => colors.dandelion);

    const shapes = match(theme)
      .returnType<Shape[]>()
      .with("hearts", () => {
        const heart = confetti.shapeFromPath(
          // a heart shape
          "M0 200V0h200a100 100 90 0 1 0 200 100 100 90 0 1-200 0z",
        );
        return [heart];
      })
      .otherwise(() => ["circle", "square"]);

    const scalar = match(theme)
      .with(
        "hearts",
        () => 1.5, // make the hearts bigger
      )
      .otherwise(() => 1);

    for (const blast of CONFETTI_BLASTS) {
      confettiRef.current({
        ...blast,
        spread: 90,
        shapes,
        scalar,
        colors: [color[50], color[100], color[200], color[300], color[400]],
      });
    }
  }, [theme, prefersReducedMotion]);

  const [viewportHeight, setViewportHeight] = useState(visualViewport?.height);

  useEffect(() => {
    const handleResize = (event: Event) => {
      if (event.target instanceof VisualViewport) {
        setViewportHeight(event.target.height);
      }
    };

    visualViewport?.addEventListener("resize", handleResize);
    return () => {
      visualViewport?.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      <div
        className="absolute inset-0 z-0 flex select-none flex-col divide-y overflow-hidden bg-[image:var(--bg-image)] bg-cover bg-center"
        style={{ height: viewportHeight }}
      >
        <div className="relative">
          <div className="grid h-header grid-cols-[1fr_auto_1fr] items-center bg-base-100/60 px-2 backdrop-blur md:px-4">
            <div className="flex gap-3.5 px-2 sm:gap-4 md:gap-6">
              <GameStat label={t("streak")} icon={Fire} value={streak} />
              <GameStat
                label={t("points")}
                icon={CoinVertical}
                value={getPoints(answers)}
              />
            </div>
            {game.timeLimit > 0 ? (
              <Timer endsAt={endsAt} onEnd={endGame} />
            ) : (
              <div />
            )}
            <div className="flex justify-end gap-1">
              <GameOption
                className="max-lg:hidden"
                icon={isRow ? SquareSplitVertical : SquareSplitHorizontal}
                onClick={() => {
                  setIsRow((prev) => !prev);
                }}
              />
              <GameOption
                className="max-sm:hidden"
                onClick={toggleSound}
                icon={isSoundEnabled ? SpeakerHigh : SpeakerX}
              />
              <GameOption
                icon={Gear}
                onClick={() => {
                  setIsMenuOpen(true);
                }}
                motionProps={{
                  variants,
                  animate: isMenuOpen ? "active" : "inactive",
                }}
              />
            </div>
          </div>
          {game.settings.isPercentShown && (
            <motion.div
              className="absolute inset-x-0 top-full z-10 box-content h-px origin-left bg-accent py-px -translate-y-px"
              animate={{ scaleX: percentCompleted }}
            />
          )}
        </div>
        <GamePlayContext
          value={{
            answers,
            addAnswer,
            blastConfetti,
            endGame,
            addRewardTime,
          }}
        >
          <div
            data-row={isRow}
            className={clsx(
              "group grid grow grid-rows-2 divide-y",
              isRow &&
                "lg:grid-cols-2 lg:grid-rows-none lg:divide-x lg:divide-y-0",
              game.mode === "classic" && "max-md:grid-rows-[3fr_2fr]",
            )}
          >
            {match(game.mode)
              .with(
                "classic",
                "mcq",
                "imcq",
                (mode) =>
                  word && (
                    <QuestionAnswer
                      mode={mode}
                      word={word}
                      isDisabled={isMenuOpen}
                    />
                  ),
              )
              .with("pairs", () => <Pairs />)
              .exhaustive()}
            <canvas
              className="pointer-events-none absolute inset-0 size-full"
              ref={(canvas) => {
                if (canvas) {
                  confettiRef.current = confetti.create(canvas, {
                    resize: true,
                  });
                }
              }}
            />
          </div>
        </GamePlayContext>
      </div>
      <div className="isolate">
        <Toaster />
      </div>
      <AnimatePresence>
        {isMenuOpen && (
          <GameMenu onClose={closeMenu}>
            <GameMenuItem onClick={closeMenu}>{t("close_menu")}</GameMenuItem>
            <GameMenuItem onClick={toggleSound}>
              {isSoundEnabled ? t("disable_sounds") : t("enable_sounds")}
            </GameMenuItem>
            <GameMenuItem onClick={restartGame}>
              {t("restart_game")}
            </GameMenuItem>
            <GameMenuItem onClick={resetGame}>{t("quit_game")}</GameMenuItem>
          </GameMenu>
        )}
      </AnimatePresence>
    </>
  );
};
